/**
 * PublishBar — top-right editor pill showing draft/publish state.
 *
 * Displays:
 *   • Amber "Unpublished changes" indicator when draft differs from published
 *   • Emerald "Published" when up-to-date
 *   • "Publish changes" button that POSTs /api/builder/publish and flips state
 *
 * Auto-polls /api/builder/publish/status every 15s and re-fetches after
 * each save-content event.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Check, Circle, Send, History } from 'lucide-react';
import { toast } from 'sonner';
import VersionHistoryModal from './VersionHistoryModal';
import PublishDiffModal from './PublishDiffModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function PublishBar() {
  const [status, setStatus] = useState({ has_unpublished_changes: false, published_at: null });
  const [publishing, setPublishing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/builder/publish/status`, { headers: authH() });
      setStatus(r.data || {});
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    const onSaved = () => refresh();
    window.addEventListener('wb:content-saved', onSaved);
    return () => { clearInterval(t); window.removeEventListener('wb:content-saved', onSaved); };
  }, [refresh]);

  const publish = async () => {
    setPublishing(true);
    try {
      await axios.post(`${API}/builder/publish`, { page_id: 'home' }, { headers: authH() });
      toast.success('Changes published to your live site');
      setStatus({ has_unpublished_changes: false, published_at: new Date().toISOString() });
      setDiffOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const dirty = !!status.has_unpublished_changes;

  return (
    <div className="inline-flex items-center gap-2" data-testid="publish-bar">
      <button
        onClick={() => setHistoryOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] uppercase tracking-[0.14em] font-bold text-white/60 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
        title="Version history — rewind any change"
        data-testid="version-history-open"
      >
        <History size={11} />
        History
      </button>
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border ${
          dirty
            ? 'border-amber-400/40 bg-amber-500/10 text-amber-300'
            : 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300'
        }`}
        data-testid={`publish-state-${dirty ? 'dirty' : 'clean'}`}
      >
        {dirty ? <Circle size={9} className="fill-amber-300" /> : <Check size={10} />}
        {dirty ? 'Unpublished changes' : 'Published'}
      </span>
      <button
        onClick={() => setDiffOpen(true)}
        disabled={publishing || !dirty}
        className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-[0.14em] transition-all ${
          !dirty
            ? 'bg-white/5 text-white/40 cursor-not-allowed'
            : 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white hover:shadow-[0_8px_20px_rgba(139,92,246,0.4)]'
        }`}
        data-testid="publish-changes-btn"
      >
        {publishing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
        Publish {dirty ? 'changes' : ''}
      </button>
      <VersionHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <PublishDiffModal open={diffOpen} onClose={() => setDiffOpen(false)} onConfirm={publish} publishing={publishing} />
    </div>
  );
}
